// The live invoice of the Pay step: the shared pricing function (`quote`) over the active rate
// cards, plus the sale of empty boxes bought with the shipment. VAT-inclusive (D-18). The server
// re-prices when it applies the command; a difference opens a `price_mismatch` exception.
import {
  money,
  quote,
  vatFromInclusive,
  type CurrencyCode,
  type PricingBox,
  type PricingCard,
  type PricingPromotion,
  type QuoteInput,
} from '@ecobox/domain';
import type { ConfigRecord } from '@ecobox/schema';
import type { City } from './cities';
import { PRIME_SIZE, primeLineLabels, primeReason, type PieceDraft } from './prime';

export interface InvoiceBox {
  barcode: string;
  sizeId: string;
  weightKg?: number | undefined;
  dims?: { l?: number | undefined; w?: number | undefined; h?: number | undefined } | undefined;
  sold: boolean;
}

export interface InvoiceLineView {
  kind: 'freight' | 'addon' | 'discount' | 'box';
  label: string;
  amountMinor: number;
  boxKey?: string | undefined;
  /** A box's freight line: what its price is based on, e.g. "Chargeable 12.5 kg: its actual weight". */
  basis?: string | undefined;
}

export type InvoiceView =
  | {
      ok: true;
      currency: CurrencyCode;
      lines: InvoiceLineView[];
      grossMinor: number;
      netMinor: number;
      vatMinor: number;
      vatRatePercent: number;
    }
  | { ok: false; reason: string };

export interface InvoiceInput {
  cards: readonly PricingCard[];
  city: City | undefined;
  /** The branch that delivers in the destination (coverage), when known. */
  servingBranchId?: string | undefined;
  boxes: readonly InvoiceBox[];
  sizes: ReadonlyMap<string, ConfigRecord<'boxSize'>>;
  declaredValueMinor: number | undefined;
  addOnCodes: readonly string[];
  vatRatePercent: number;
  currency: CurrencyCode;
  /** Price of an empty box by size at this branch, for boxes sold with the shipment. */
  emptyBoxPrices: ReadonlyMap<string, number>;
  /** The Prime pieces, when the boxes are Prime pieces (their lines are named after the piece). */
  pieces?: readonly PieceDraft[] | undefined;
  /** Promotions to try and the sale they are checked against (./promotions.ts), as the server does. */
  promotions?: readonly PricingPromotion[] | undefined;
  sale?: QuoteInput['sale'];
}

export function fullDims(
  dims: InvoiceBox['dims'],
): { l: number; w: number; h: number } | undefined {
  return dims && dims.l && dims.w && dims.h ? { l: dims.l, w: dims.w, h: dims.h } : undefined;
}

export function computeInvoice(input: InvoiceInput): InvoiceView {
  if (!input.city) return { ok: false, reason: 'Choose the destination city first.' };
  const boxes = input.boxes.filter((b) => b.sizeId !== '');
  if (boxes.length === 0) return { ok: false, reason: 'Add at least one box to see the price.' };
  if (input.cards.length === 0) {
    return {
      ok: false,
      reason:
        'There is no active rate card for this branch and service. Ask the office to publish one.',
    };
  }

  const pricingBoxes: PricingBox[] = boxes.map((b) => {
    const dims = fullDims(b.dims);
    return {
      key: b.barcode,
      sizeId: b.sizeId,
      ...(b.weightKg !== undefined ? { weightKg: b.weightKg } : {}),
      ...(dims ? { dimsCm: dims } : {}),
    };
  });
  const sizes = new Map(
    [...input.sizes].map(([code, s]) => [
      code,
      {
        ...(s.quoteWeightKg !== undefined ? { quoteWeightKg: s.quoteWeightKg } : {}),
        ...(s.dimsCm ? { dimsCm: s.dimsCm } : {}),
      },
    ]),
  );

  let result: ReturnType<typeof quote>;
  try {
    result = quote({
      cards: input.cards,
      destination: {
        cityId: input.city.id,
        ...(input.city.routeCode ? { routeCode: input.city.routeCode } : {}),
        ...(input.city.zoneId ? { zoneId: input.city.zoneId } : {}),
        ...(input.servingBranchId ? { branchId: input.servingBranchId } : {}),
        country: input.city.country,
      },
      boxes: pricingBoxes,
      sizes,
      ...(input.declaredValueMinor !== undefined
        ? { declaredValueMinor: input.declaredValueMinor }
        : {}),
      addOnCodes: input.addOnCodes,
      vatRatePercent: input.vatRatePercent,
      ...(input.promotions && input.sale ? { promotions: input.promotions, sale: input.sale } : {}),
    });
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : 'The price could not be worked out.',
    };
  }
  if (!result.ok) return { ok: false, reason: primeReason(result.reason) };
  if (result.currency !== input.currency) {
    return {
      ok: false,
      reason: `The rate card is in ${result.currency} but this branch works in ${input.currency}. Ask the office to fix the rate card.`,
    };
  }

  const rawLines: InvoiceLineView[] = result.lines.map((l) => ({
    kind: l.kind,
    label: l.label,
    amountMinor: l.amount.amountMinor,
    ...(l.boxKey ? { boxKey: l.boxKey } : {}),
    ...(l.basis ? { basis: l.basis } : {}),
  }));
  const lines =
    input.pieces && boxes.every((b) => b.sizeId === PRIME_SIZE)
      ? primeLineLabels(rawLines, input.pieces)
      : rawLines;
  for (const b of boxes.filter((x) => x.sold)) {
    const price = input.emptyBoxPrices.get(b.sizeId);
    if (price === undefined) {
      return {
        ok: false,
        reason: `No price is set for an empty box of size ${b.sizeId} at this branch (Settings → Empty box prices).`,
      };
    }
    lines.push({
      kind: 'box',
      label: `Empty box ${b.sizeId} (sold now)`,
      amountMinor: price,
      boxKey: b.barcode,
    });
  }

  const grossMinor = lines.reduce((n, l) => n + l.amountMinor, 0);
  const split = vatFromInclusive(money(grossMinor, input.currency), input.vatRatePercent);
  return {
    ok: true,
    currency: input.currency,
    lines,
    grossMinor,
    netMinor: split.net.amountMinor,
    vatMinor: split.vat.amountMinor,
    vatRatePercent: input.vatRatePercent,
  };
}
